import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Grid, Typography, Stack, TextField, InputAdornment, Autocomplete, ClickAwayListener, Popper, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import CapacityGrowthChart from './CapacityGrowthChart';
import { CustomizedDataGrid } from '../../customized_data_grid';
import { Column } from 'react-data-grid';
import LoaderComponent from 'components/Loader';
import NumberWidget from 'components/Widgets/NumberWidget';
import { 
  fetchPowerBICapacityKpi, 
  fetchPowerBICapacityGrowthData, 
  fetchPowerBICapacityTableData, 
  fetchPowerBIExportCapacityDetails,
  fetchCapacityNames, 
  getPepwiseSectorList,
  fetchUserActivityFilterOptions
} from 'service/service';
import moment from 'moment';
import {
  CapacityIdFormatter,
  CapacityNameFormatter,
  StatusFormatter,
  LastRefreshDateFormatter,
  FailureReasonFormatter,
  SkuFormatter,
  SectorFormatter,
  DatasetNameFormatter,
  RefreshScheduleFormatter,
  LastRefreshDurationFormatter,
  WorkspaceNameFormatter,
  LastRefreshStatusFormatter,
  OperationFormatter,
  UserEmailFormatter,
  OperationTimeFormatter,
  ReportNameFormatter,
  WorkspaceFormatter,
  CapacityOwnerFormatter,
  WorkspaceOwnerFormatter
} from './formatters';
import { useTheme } from "@mui/material/styles";

interface Row {
  id: string;
  capacityId: string;
  capacityName: string;
  status: string;
  sku: string;
  sector: string;
  capacityOwner: string;
  workspace: string;
  workspaceOwner: string;
  reportName: string;
  capacityStatus: string;
  isThrottled?: boolean;
  lastRefreshStatus?: string;
  lastRefreshDuration?: string;
  refreshType?: string;
  operation?: string;
  userEmail?: string;
  operationTime?: string;
  datasetName?: string;
}

interface CapacityOption {
  capacityId: string;
  capacityName: string;
}

interface CapacityTabProps {
  filters?: {
    sector?: string | string[];
    filterOption?: string;
    startDate?: string;
    endDate?: string;
    filterList?: null | { [key: string]: string };
  };
  timeRange?: string;
  sectorsChange?: string;
  checkedListRef?: React.MutableRefObject<any>;
}

interface PayloadParams {
  filters: any;
  pageSize: number;
  sortingInfo: any;
  timeRangeRef: React.MutableRefObject<string | undefined>;
  kpiType: string;
  targetPage: number;
  defaultSortBy: string;
  defaultOrderBy?: string;
  searchAfter?: any;
  selectedCapacity?: any;
  checkedListRef?:any;
  allSectors?:any;
  filterInfo?: { [key: string]: any },
  totalPages?:number;
}

const normalizeSectors = (sectorList: string[]) => {
  let sectors = sectorList.map(s => s.trim().toUpperCase());
  const hasAMESA = sectors.includes('AMESA');
  const hasAPAC = sectors.includes('APAC');
  
  if (hasAMESA && hasAPAC) {
    sectors = sectors.filter(s => s !== 'AMESA' && s !== 'APAC');
    sectors.push('AMESA/APAC');
  }
  
  return sectors
    .map(s => (s === 'EUROPE' ? 'Europe' : s))
    .join(',');
};

const isValuePresent = (value: unknown): boolean => 
  Array.isArray(value) ? value.length > 0 : value != null;

const getCleanFilters = (filterInfo: { [key: string]: any }) => 
  Object.fromEntries(
    Object.entries(filterInfo).filter(([, value]) => isValuePresent(value))
  );

const createPayload = (params: PayloadParams) => {
  const { 
    filters, 
    pageSize, 
    sortingInfo, 
    timeRangeRef, 
    kpiType, 
    targetPage, 
    defaultSortBy, 
    defaultOrderBy = 'asc',
    searchAfter = null,
    selectedCapacity = null,
    filterInfo = {},
    checkedListRef,
    allSectors, totalPages
  } = params;

  const getSelectedSectorsList = () =>
    checkedListRef?.current?.length ? checkedListRef.current : allSectors;

  const resolveSectorValue = () => {
    const hasCustomSector = filters?.sector && filters?.sector !== 'all';
    const sectorSource = hasCustomSector ? filters.sector.split(',') : getSelectedSectorsList();
    return normalizeSectors(sectorSource);
  };

  const buildColumnFilters = () => {
    const cleanFilters = getCleanFilters(filterInfo);
    return Object.keys(cleanFilters).length > 0 ? cleanFilters : filters?.filterList;
  };

  const payload: any = {
    ...filters,
    sector: resolveSectorValue(),
    page: targetPage,
    pageSize,
    kpiType,
    sortBy: sortingInfo.sortBy || defaultSortBy,
    orderBy: sortingInfo.sortDir || defaultOrderBy,
    capacityName: selectedCapacity?.capacityName || null,
    filterOption: timeRangeRef.current,
    totalPages: totalPages
  };

  const columnFilters = buildColumnFilters();
  if (columnFilters) {
    payload.filters = columnFilters;
  }

  if (searchAfter) {
    payload.searchAfter = searchAfter;
  }

  return payload;
};


// Common response handling function
const handleTableResponse = (
  response: any,
  targetPage: number,
  pageSize: number,
  setPaginationData: any,
  dataKey: string,
  transformFn: (item: any) => any
) => {
  if (response?.data?.success) {
    const dataItems = response.data.data?.[dataKey] || [];
    const total = response.data.data?.pagination?.totalItems || dataItems.length;
   
    setPaginationData({ 
      currentPage: targetPage, 
      totalPages: Math.ceil(total / pageSize), 
      totalElements: total,
      pageSize: pageSize 
    });

    return dataItems.map(transformFn);
  }
  return [];
};

// Common error handling wrapper
const withErrorHandling = async <T,>(fn: () => Promise<T>, errorMessage: string): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    console.error(errorMessage, error);
    return [] as T;
  }
};

const CapacityTab: React.FC<CapacityTabProps> = ({ 
  filters, 
  timeRange, 
  sectorsChange,
  checkedListRef
}) => {
  const theme = useTheme();
  // Dynamic data states
  const [kpiData, setKpiData] = useState<any>({
    healthyCapacities: { value: 0, change: 0, changeType: 'neutral' },
    throttledCapacities: { value: 0, change: 0, changeType: 'neutral' }
  });
  const [subCardsData, setSubCardsData] = useState<any[]>([]);
  const [capacityGrowthData, setCapacityGrowthData] = useState<any[]>([]);
  const [isKpiLoading, setIsKpiLoading] = useState<boolean>(true);
  const [isGrowthLoading, setIsGrowthLoading] = useState<boolean>(true);
  const [searchAfterMap, setSearchAfterMap] = useState(new Map());

  // Capacity Names state
  const [capacityOptions, setCapacityOptions] = useState<CapacityOption[]>([]);
  const [capacityOptionsLoading, setCapacityOptionsLoading] = useState<boolean>(false);
  const [selectedCapacity, setSelectedCapacity] = useState<CapacityOption | null>(null);
  const [capacitySearchAnchorEl, setCapacitySearchAnchorEl] = useState<HTMLElement | null>(null);

  // Table state management
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [paginationData, setPaginationData] = useState({
    currentPage: page,
    totalPages: 0,
    totalElements: 0,
    pageSize: pageSize
  });
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [filterInfo, setFilterInfo] = useState<{ [key: string]: any }>({});


  const [filterDataOptions, setFilterDataOptions] = useState<any>(null);

  // Add search state


  // KPI State Management
  const [activeKPI, setActiveKPI] = useState<string>('');
  const [kpiTableData, setKpiTableData] = useState<any[]>([]);
  const [kpiTableLoading, setKpiTableLoading] = useState<boolean>(false);
  const [kpiTableTitle, setKpiTableTitle] = useState<string>('Capacity Monitoring');

  // Filter states
  const [selectedCard, setSelectedCard] = useState<string>('');

  const [selectedChartSegment, setSelectedChartSegment] = useState<string>('');
  const [chartTimePeriod, setChartTimePeriod] = useState<string>('Monthly');
  const [allSectors, setAllSectors] = useState<string[]>([]);
  const [sectorsLoaded, setSectorsLoaded] = useState<boolean>(false);

  const timeRangeRef = useRef<string | undefined>(timeRange);

  // Ref to guard filter options from running before critical APIs
  const filterOptionsFetchedRef = useRef<boolean>(false);

  // Ref to skip initial mount for subsequent-change effects
  const isInitialMountRef = useRef<boolean>(true);

  // Add ref to track dependency changes
  interface FetchState {
    page: number;
    pageSize: number;
    sortingInfo: string;
    timeRange: string | undefined;
    filterInfo: string;
    sector: string | string[] | undefined;
    activeKPI: string | null;
    sectorsChange: string | undefined;
  }

  const lastFetchRef = useRef<FetchState>({
    page: 1,
    pageSize: 100,
    sortingInfo: JSON.stringify({ sortBy: '', sortDir: '' }),
    timeRange: undefined,
    filterInfo: JSON.stringify({}),
    sector: undefined,
    activeKPI: 'Healthy',
    sectorsChange: undefined
  });

  // ─── fetchFilterOptions ───────────────────────────────────────────────────
  // Extracted as useCallback so it can be called manually AFTER critical APIs.
  // A guard ref ensures it only runs once per session.
  const fetchFilterOptions = useCallback(async () => {
    if (filterOptionsFetchedRef.current) return;
    filterOptionsFetchedRef.current = true; // Mark immediately to prevent concurrent calls

    const extractData = (response: any) => response?.data || [];

    try {
      const sectors = await getPepwiseSectorList();
      const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];

      const [capacityNames, capacityId, sku, workspaceName, workspace, datasetName,
        dataset, lastRefreshDuration, refreshSchedule, operation, user_email, ReportName] =
        await Promise.all([
        fetchUserActivityFilterOptions('capacityName', '', 'inactive'),
        fetchUserActivityFilterOptions('capacityId', '', 'inactive'),
        fetchUserActivityFilterOptions('sku', '', 'inactive'),
        fetchUserActivityFilterOptions('workspaceName', '', 'active'),
        fetchUserActivityFilterOptions('workspace', '', 'active'),
        fetchUserActivityFilterOptions('datasetName', '', 'active'),
        fetchUserActivityFilterOptions('dataset', '', 'active'),
        fetchUserActivityFilterOptions('lastRefreshDuration', '', 'active'),
        fetchUserActivityFilterOptions('refreshSchedule', '', 'active'),
        fetchUserActivityFilterOptions('operation', '', 'active'),
        fetchUserActivityFilterOptions('user_email', '', 'active'),
        fetchUserActivityFilterOptions('ReportName', '', 'active')
      ]);

      setFilterDataOptions({
        capacityName: extractData(capacityNames),
        sector: filterSectors,
        capacityId: extractData(capacityId),
        sku: extractData(sku),
        workspaceName: extractData(workspaceName),
        datasetName: extractData(datasetName),
        lastRefreshDuration: extractData(lastRefreshDuration),
        refreshSchedule: extractData(refreshSchedule),
        workspace: extractData(workspace),
        DatasetName: extractData(dataset),
        operation: extractData(operation),
        userEmail: extractData(user_email),
        reportName: extractData(ReportName),
        CapacityNames: extractData(capacityNames),
        DatasetNames: extractData(datasetName)
      });
    } catch (error) {
      // Reset guard on failure so it can be retried
      filterOptionsFetchedRef.current = false;
      console.error('Error fetching filter options for Capacity tab:', error);
    }
  }, []);

  useEffect(() => {
    timeRangeRef.current = timeRange;
    console.log('⏰ timeRangeRef updated to:', timeRangeRef.current);
  }, [timeRange]);

  // NEW: Fetch Capacity Names from API
  const fetchCapacityOptionsFromAPI = async () => {
    setCapacityOptionsLoading(true);
    try {
      console.log('Fetching capacity names from API...');
      const response = await fetchCapacityNames();

      if (response?.success && response?.data) {
        const capacities = response.data.capacities || [];
        console.log(`Loaded ${capacities.length} capacity options`);
        setCapacityOptions(capacities);
      } else {
        console.error('Failed to fetch capacity names:', response);
        setCapacityOptions([]);
      }
    } catch (error) {
      console.error('Error fetching capacity names:', error);
      setCapacityOptions([]);
    } finally {
      setCapacityOptionsLoading(false);
    }
  };

  const formatValue = (value?: number): string => {
    if (!value) return '0';
  
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return value.toString();
  };

  // API call functions
  const fetchKPIData: any = async () => {
    if (!sectorsLoaded) return;
    setIsKpiLoading(true);
    const rawSectors = checkedListRef?.current.length > 0 
      ? checkedListRef?.current 
      : allSectors;

    // Remove duplicates and handle AMESA/APAC logic
    const uniqueSectors = Array.from(new Set(rawSectors));

    const expandedSectors = uniqueSectors.flatMap((sector: any) => {
      
      if (sector === "AMESA/APAC") {
        return ["AMESA/APAC"];
      }
      
      if (sector === "AMESA") {
        return ["AMESA", "AMESA/APAC"];
      }
      
      if (sector === "APAC") {
        return ["APAC", "AMESA/APAC"];
      }
      
      return [sector];
    });

    // Remove duplicates from the final result
    const selectedSectors = Array.from(new Set(expandedSectors)).join(',');
    try {
      const filtersParam = {
        sector: selectedSectors,
        filterOption: filters?.filterOption || timeRange,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        capacityName: selectedCapacity	
      };

      console.log('Fetching capacity KPI data with params:', filtersParam);
      const response = await fetchPowerBICapacityKpi(filtersParam);

      if (response && response.success !== false && response.data) {
        const data = response.data;

        setKpiData({
          healthyCapacities: {
            value: data.capacityHealthy?.value || 0,
            change: data.capacityHealthy?.change || 0,
            changeType: data.capacityHealthy?.changeType || 'neutral'
          },
          throttledCapacities: {
            value: data.capacityThrottled?.value || 0,
            change: data.capacityThrottled?.change || 0,
            changeType: data.capacityThrottled?.changeType || 'neutral'
          }
        });

        if (data.fabricCapacityMetrics) {
          const metrics = data.fabricCapacityMetrics;
          const fabricMetrics = [
            {
              title: 'Scheduled Refreshes',
              value: metrics.scheduledRefreshes?.percentage?.toLocaleString() || '0',
              unit: '%',
              filterType: null,
              change: metrics.scheduledRefreshes?.change || 0,
              changeType: metrics.scheduledRefreshes?.changeType || 'neutral',
              toolTipText: 'Percentage of total dataset refreshes triggered via schedule within the selected date range'
            },
            {
              title: 'On-Demand Refreshes',
              value: metrics.onDemandRefreshes?.percentage?.toLocaleString() || '0',
              unit: '%',
              filterType: null,
              change: metrics.onDemandRefreshes?.change || 0,
              changeType: metrics.onDemandRefreshes?.changeType || 'neutral',
              toolTipText: 'Percentage of total dataset refreshes triggered manually within the selected date range'
            },
            {
              title: 'Failed Refreshes',
              value: metrics.failedRefreshes?.value?.toLocaleString() || '0',
              unit: '',
              filterType: null,
              change: metrics.failedRefreshes?.change || 0,
              changeType: metrics.failedRefreshes?.changeType || 'neutral',
              toolTipText: 'Total number of dataset refreshes which have failed within the selected date range'
            },
            {
              title: 'Refreshes > 30Mins',
              value: metrics.longerRefresh?.value?.toLocaleString() || '0',
              unit: '',
              filterType: null,
              change: metrics.longerRefresh?.change || 0,
              changeType: metrics.longerRefresh?.changeType || 'neutral',
              toolTipText: 'Total number of dataset refreshes which have ran for more than 30 minutes within the selected date range'
            },
            {
              title: 'Memory Config',
              value: metrics.capacityMaxMemory?.maxMemory?.toString() || '0',
              unit: metrics.capacityMaxMemory?.unit || 'GB',
              filterType: null,
              change: 0,
              changeType: 'neutral',
              toolTipText: 'Maximum memory of the selected capacity'
            },
            {
              title: 'Max Dataset Size',
              value: metrics.capacityMaxItemSize?.value?.toFixed(2) || '0',
              unit: 'GB',
              filterType: null,
              change: metrics.capacityMaxItemSize?.change || 0,
              changeType: metrics.capacityMaxItemSize?.changeType || 'neutral',
              toolTipText: 'Maximum size of a dataset as per Fabric Capacity Metrics of the selected capacity within the selected date range'
            },
            {
              title: 'Concurrent Users',
              value: metrics.concurrentUsers?.value?.toLocaleString() || '0',
              unit: '',
              filterType: null,
              change: metrics.concurrentUsers?.change || 0,
              changeType: metrics.concurrentUsers?.changeType || 'neutral',
              toolTipText: 'Maximum number of users across all 30-sec time windows within the selected date range'
            },
            {
              title: 'XMLA Reads',
              value: formatValue(metrics.xmlaReads?.value) || '0',
              unit: '',
              filterType: null,
              change: metrics.xmlaReads?.change || 0,
              changeType: metrics.xmlaReads?.changeType || 'neutral',
              toolTipText: [
                {
                  title: '',
                  description: (
                    <Box>
                      <Typography variant="bodyText">
                        Total number of XMLA Read operations within the selected date range. The activity types included are: <strong>*Analyze*</strong>, <strong>*XMLA*</strong>
                      </Typography>
                      <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', marginTop: '8px' }}>
                        <li>AnalyzeInExcelDataset</li>
                        <li>CreateDatasetFromExternalApplication</li>
                        <li>ConnectFromExternalApplication</li>
                        <li>AnalyzeInExcelReport</li>
                        <li>DeleteDatasetFromExternalApplication</li>
                        <li>EditDatasetFromExternalApplication</li>
                        <li>RefreshDatasetFromExternalApplication</li>
                      </Box>
                    </Box>
                  )
                }
              ]
            },
            {
              title: 'Export Operations',
              value: formatValue(metrics.exportOperations?.value) || '0',
              unit: '',
              filterType: null,
              change: metrics.exportOperations?.change || 0,
              changeType: metrics.exportOperations?.changeType || 'neutral',
              toolTipText: 'Total number of export operations within the selected date range. The activity types included are: Export*'
            }
          ];
          setSubCardsData(fabricMetrics);
        }
      }
    } catch (error) {
      console.error('Error fetching capacity KPI data:', error);
    } finally {
      setIsKpiLoading(false);
    }
  };

  // Memoized fetchGrowthData to prevent recreation on every render
  const getSelectedSectors = () => (
    checkedListRef?.current?.length > 0
      ? checkedListRef?.current?.join(',') || ''
      : allSectors.join(',')
  );
  const extractChartData = (response: any) => {
    const isValidResponse = response && response.success !== false && response.data;
      if (!isValidResponse) {
        return null;
      }
  
    const nestedData = response.data.data || response.data;
    const chartData = nestedData.chartData;
    return Array.isArray(chartData) ? chartData : null;
  };

  const transformChartData = (chartData: any[]) => chartData.map((item: any) => ({
    month: item.period,
    PBNA: item.PBNA || 0,
    PFNA: item.PFNA || 0,
      AMESA_APAC: item['AMESA/APAC'] || 0,  // Handle both "AMESA/APAC" and "AMESA_APAC"
    CGF: item.CGF || 0,
    LATAM: item.LATAM || 0,
      EUROPE: item.EUROPE || item.Europe || 0  // Handle both "EUROPE" and "Europe"
  }));

  const fetchGrowthData = useCallback(async (customTimePeriod?: string) => {
    if (!sectorsLoaded) return;
    setIsGrowthLoading(true);

    const buildFiltersParam = (selectedSectors: string) => ({
      sector: selectedSectors,
      chartTimePeriod: customTimePeriod || chartTimePeriod,
      filterOption: filters?.filterOption || timeRange
    });


    try {
      const selectedSectors = getSelectedSectors();
      const filtersParam = buildFiltersParam(selectedSectors);

      console.log('Fetching capacity growth data with params:', filtersParam);
      const response = await fetchPowerBICapacityGrowthData(filtersParam);

      const chartData = extractChartData(response);
      if (!chartData) return;

      const transformedData = transformChartData(chartData);
      console.log('Transformed chart data:', transformedData);
      setCapacityGrowthData(transformedData);
    } catch (error) {
      console.error('Error fetching capacity growth data:', error);
    } finally {
      setIsGrowthLoading(false);
    }
  }, [sectorsLoaded, checkedListRef, allSectors, chartTimePeriod, filters?.filterOption, timeRange]);

  // KPI Data Fetching Functions - Refactored to reduce cognitive complexity
  const fetchHealthyCapacities = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching healthy capacities with timeRange:', timeRangeRef.current);

    return withErrorHandling(async () => {
      const payload = createPayload({
        filters,
        pageSize,
        sortingInfo,
        timeRangeRef,
        kpiType: 'healthyCapacities',
        targetPage,
        defaultSortBy: 'capacityName',
        defaultOrderBy: 'asc',
        searchAfter: null,
        selectedCapacity,
        filterInfo,
        checkedListRef,
        allSectors
      });
      const response = await fetchPowerBICapacityTableData(payload);
      
      return handleTableResponse(response, targetPage, pageSize, setPaginationData, 'capacities', (capacity: any) => ({
        id: capacity.capacityId || Math.random().toString(),
        capacityId: capacity.capacityId || '',
        capacityName: capacity.capacity || 'Unknown',
        status: 'Healthy',
        sku: capacity.sku || 'Unknown',
        sector: capacity.sector || 'Unknown',
        capacityOwner: capacity.capacityOwner || 'Unknown',
        workspace: capacity.workspace || 'Unknown Workspace',
        workspaceOwner: capacity.workspaceOwner || '--',
        reportName: capacity.reportName || 'Unknown Report',
        capacityStatus: 'healthy'
      }));
    }, 'Error fetching healthy capacities:');
  };

  const fetchThrottledCapacities = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching throttled capacities with timeRange:', timeRangeRef.current);
    
    return withErrorHandling(async () => {
      const payload = createPayload({
        filters,
        pageSize,
        sortingInfo,
        timeRangeRef,
        kpiType: 'throttledCapacities',
        targetPage,
        defaultSortBy: 'capacityName',
        defaultOrderBy: 'asc',
        checkedListRef,
        allSectors,
        selectedCapacity,
        filterInfo
      });
      const response = await fetchPowerBICapacityTableData(payload);
      
      return handleTableResponse(response, targetPage, pageSize, setPaginationData, 'capacities', (capacity: any) => ({
        id: capacity.capacityId || Math.random().toString(),
        capacityId: capacity.capacityId || '',
        capacityName: capacity.capacity || 'Unknown',
        status: 'Throttled',
        sku: capacity.sku || 'Unknown',
        sector: capacity.sector || 'Unknown',
        capacityOwner: capacity.capacityOwner || 'Unknown',
        workspace: capacity.workspace || 'Unknown Workspace',
        workspaceOwner: capacity.workspaceOwner || '--',
        reportName: capacity.reportName || 'Unknown Report',
        capacityStatus: 'throttled'
      }));
    }, 'Error fetching throttled capacities:');
  };

  const fetchFailedRefreshes = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching failed refreshes with timeRange:', timeRangeRef.current);
    
    return withErrorHandling(async () => {
      const payload = createPayload({
        filters,
        pageSize,
        sortingInfo,
        timeRangeRef,
        kpiType: 'failedRefreshes',
        targetPage,
        defaultSortBy: 'datasetName',
        defaultOrderBy: 'desc',
        selectedCapacity,
        checkedListRef,
        allSectors,
        filterInfo
      });
      const response = await fetchPowerBICapacityTableData(payload);
      
      return handleTableResponse(response, targetPage, pageSize, setPaginationData, 'datasets', (dataset: any) => ({
        id: dataset.datasetId || Math.random().toString(),
        datasetName: dataset.dataset || 'Unknown',
        refreshSchedule: dataset.refreshSchedule || '--',
        lastRefreshDuration: dataset.lastRefreshDuration || '--',
        capacityName: dataset.capacity || 'Unknown',
        workspaceName: dataset.workspace || 'Unknown',
        lastRefreshStatus: 'Failed',
        lastRefreshTime: dataset.lastRefreshTime || '--',
        failureReason: dataset.failureReason || '--',
        refreshType: dataset.refreshType || '--'
      }));
    }, 'Error fetching failed refreshes:');
  };

  const fetchRefreshesOver30Min = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching refreshes over 30min with timeRange:', timeRangeRef.current);
    const prevPageKey = `refreshesOver30Min-${targetPage - 1}`;
    const searchAfter = searchAfterMap.get(prevPageKey);
    return withErrorHandling(async () => {
      const payload = createPayload({
        filters,
        pageSize,
        sortingInfo,
        timeRangeRef,
        kpiType: 'refreshesOver30Min',
        targetPage,
        defaultSortBy: 'startTime',
        defaultOrderBy: 'desc',
        searchAfter,  // Pass searchAfter for sequential
        selectedCapacity,
        checkedListRef,
        allSectors,
        filterInfo
      });

      if (Math.abs(targetPage - page) > 1) {
        payload.isPageJump = true;
        payload.jumpDistance = Math.abs(targetPage - page);
      }
      const response = await fetchPowerBICapacityTableData(payload);
      
      return handleTableResponse(response, targetPage, pageSize, setPaginationData, 'datasets', (dataset: any) => ({
        id: dataset.datasetId || Math.random().toString(),
        datasetName: dataset.dataset || 'Unknown',
        refreshSchedule: dataset.refreshSchedule || '--',
        lastRefreshDuration: dataset.lastRefreshDuration || '--',
        capacityName: dataset.capacity || 'Unknown',
        workspaceName: dataset.workspace || 'Unknown',
        lastRefreshStatus: dataset.lastRefreshStatus || '--',
        lastRefreshTime: dataset.lastRefreshTime || '--',
        failureReason: dataset.failureReason || '--',
        refreshType: dataset.refreshType || '--'
      }));
    }, 'Error fetching refreshes over 30min:');
  };

  const fetchExportOperations = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching Export Operations with timeRange:', timeRangeRef.current);

    const kpiType = `exportOperations`;
    const searchAfterKey = `${kpiType}-${targetPage - 1}`;
    const searchAfter = searchAfterMap.get(searchAfterKey);
    searchAfterMap.delete(searchAfterKey);

    const payload = createPayload({
      filters,
      pageSize,
      sortingInfo,
      timeRangeRef,
      kpiType: kpiType, 
      targetPage,
      defaultSortBy: 'creationTime',
      defaultOrderBy: 'desc',
      searchAfter,
      selectedCapacity,checkedListRef,allSectors,
      filterInfo, totalPages:paginationData.totalPages
    });

    try {
      const response = await fetchPowerBICapacityTableData(payload);

      if (response?.data?.success) {
        const activities = response.data.data?.activities || [];
        const total = response.data.data?.pagination?.totalItems || activities.length;

        if (response.data.data.searchAfter) {
          const currentPageKey = `exportOperations-${targetPage}`;
          setSearchAfterMap(prev => new Map(prev).set(currentPageKey, response.data.data.searchAfter));
          console.log('Stored searchAfter for page:', targetPage, response.data.data.searchAfter);
        }

        setPaginationData({ 
          currentPage: targetPage, 
          totalPages: Math.ceil(total / pageSize), 
          totalElements: total,
          pageSize: pageSize 
        });

      // Transform data for table
        return activities.map((activity: any) => ({
          id: activity.activityId || Math.random().toString(),
          operation: activity.operation || 'Unknown',
          userEmail: activity.userEmail || 'Unknown',
          operationTime: activity.operationTime || '--',
          reportName: activity.report || 'Unknown',
          workspace: activity.workspace || 'Unknown',
          capacityName: activity.capacity || 'Unknown',
          datasetName: activity.dataset || 'Unknown'
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching Export Operations:', error);
      return [];
    }
  };
  

  const fetchXmlaReads = async (targetPage: number = page): Promise<any[]> => {
    console.log('Fetching XMLA reads with timeRange:', timeRangeRef.current);
    
    return withErrorHandling(async () => {
      const payload = createPayload({
        filters,
        pageSize,
        sortingInfo,
        timeRangeRef,
        kpiType: 'xmlaReads',
        targetPage,
        defaultSortBy: 'creationTime',
        defaultOrderBy: 'desc',
        selectedCapacity,
        checkedListRef,
        allSectors,
        filterInfo
      });
      const response = await fetchPowerBICapacityTableData(payload);
      
      return handleTableResponse(response, targetPage, pageSize, setPaginationData, 'activities', (activity: any) => ({
        id: activity.activityId || Math.random().toString(),
        operation: activity.operation || 'Unknown',
        userEmail: activity.userEmail || 'Unknown',
        operationTime: activity.operationTime || '--',
        reportName: activity.report || 'Unknown',
        workspace: activity.workspace || 'Unknown',
        capacityName: activity.capacity || 'Unknown',
        datasetName: activity.dataset || 'Unknown'
      }));
    }, 'Error fetching XMLA reads:');
  };

  // Handle KPI card clicks
  const handleKPICardClick = useCallback(async (cardTitle: string, shouldSetActive: boolean = true, targetPage: number = page) => {
    console.log('KPI Card clicked:', cardTitle, 'page:', targetPage, 'timeRange:', timeRangeRef.current);

    if (shouldSetActive && activeKPI !== cardTitle) {
      setPage(1);
      targetPage = 1; 
    }

    if (shouldSetActive) {
      setSelectedCard(cardTitle);
      setActiveKPI(cardTitle);
    }

    setKpiTableLoading(true);

    try {
      let tableData: any[] = [];
      let title = `Details – ${cardTitle}`;

      switch (cardTitle) {
        case 'Healthy':
          tableData = await fetchHealthyCapacities(targetPage);
          break;
        case 'Throttled':
          tableData = await fetchThrottledCapacities(targetPage);
          break;
        case 'Failed Refreshes':
          tableData = await fetchFailedRefreshes(targetPage);
          break;
        case 'Refreshes > 30Mins':
          tableData = await fetchRefreshesOver30Min(targetPage);
          break;
        case 'XMLA Reads':
          tableData = await fetchXmlaReads(targetPage);
          break;
        case 'Export Operations':
          tableData = await fetchExportOperations(targetPage);
          break
        default:
          tableData = [];
      }

      setKpiTableData(tableData);
      setKpiTableTitle(title);
    } catch (error) {
      console.error(`Error fetching ${cardTitle} data:`, error);
      setKpiTableData([]);
    } finally {
      setKpiTableLoading(false);
    }
  }, [selectedCapacity, page, activeKPI, filters, pageSize, sortingInfo, filterInfo]);

  // Common column configurations
  const commonCapacityColumns: Array<Column<any>> = [
    {
      key: 'capacityId',
      name: 'Capacity Id',
      minWidth: 200,
      formatter: CapacityIdFormatter,
    },
    {
      key: 'capacityName',
      name: 'Capacity Name',
      minWidth: 200,
      formatter: CapacityNameFormatter,
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 120,
      formatter: StatusFormatter,
		},
    {
      key: 'sku',
      name: 'SKU',
      minWidth: 120,
      formatter: SkuFormatter,
    },
    {
      key: 'sector',
      name: 'Sector',
      minWidth: 120,
      formatter: SectorFormatter,
    }
  ];

  const commonDatasetColumns: Array<Column<any>> = [
    {
      key: 'datasetName',
      name: 'Dataset Name',
      minWidth: 200,
      formatter: DatasetNameFormatter,
    },
    {
      key: 'refreshSchedule',
      name: 'Refresh Schedule',
      minWidth: 180,
      width: 250,
      formatter: RefreshScheduleFormatter,
    },
    {
      key: 'lastRefreshDuration',
      name: 'Last Refresh Duration',
      minWidth: 180,
      formatter: LastRefreshDurationFormatter,
    },
    {
      key: 'capacityName',
      name: 'Capacity Name',
      minWidth: 180,
      formatter: CapacityNameFormatter,
    },
    {
      key: 'workspaceName',
      name: 'Workspace Name',
      minWidth: 200,
      formatter: WorkspaceNameFormatter,
    },
    {
      key: 'lastRefreshStatus',
      name: 'Refresh Status',
      minWidth: 150,
      formatter: LastRefreshStatusFormatter,
    },
    {
      key: 'lastRefreshTime',
      name: 'Last Refresh Start (UTC)',
      minWidth: 150,
      formatter: LastRefreshDateFormatter,
    }
  ];

  const failedRefreshColumn: Array<Column<any>> = [...commonDatasetColumns, {
      key: 'failureReason',
      name: 'Failure Reason',
      width: 200,
      formatter: FailureReasonFormatter
  }];

  const xmlaReadsColumns: Array<Column<any>> = [
    {
      key: 'operation',
      name: 'Operation',
      minWidth: 180,
      formatter: OperationFormatter,
    },
    {
      key: 'userEmail',
      name: 'User Email',
      minWidth: 200,
      formatter: UserEmailFormatter,
    },
    {
      key: 'operationTime',
      name: 'Operation Time',
      minWidth: 180,
      formatter: OperationTimeFormatter,
    },
    {
      key: 'reportName',
      name: 'Report Name',
      minWidth: 200,
      formatter: ReportNameFormatter,
    },
    {
      key: 'workspace',
      name: 'Workspace',
      minWidth: 180,
      formatter: WorkspaceFormatter,
    },
    {
      key: 'CapacityNames',
      name: 'Capacity Name',
      minWidth: 180,
      formatter: CapacityNameFormatter,
    },
    {
      key: 'DatasetNames',
      name: 'Dataset Name',
      minWidth: 200,
      formatter: DatasetNameFormatter,
    }
  ];

  // Default columns
  const defaultColumns: Array<Column<any>> = [
    {
      key: 'sector',
      name: 'Sector',
      minWidth: 120,
      formatter: SectorFormatter,
    },
    {
      key: 'capacity',
      name: 'Capacity',
      minWidth: 140,
      formatter: CapacityNameFormatter,
    },
    {
      key: 'capacityOwner',
      name: 'Capacity Owner',
      minWidth: 160,
      formatter: CapacityOwnerFormatter,
    },
    {
      key: 'capacitySKU',
      name: 'Capacity SKU',
      minWidth: 120,
      formatter: SkuFormatter,
    },
    {
      key: 'workspace',
      name: 'Workspace',
      minWidth: 140,
      formatter: WorkspaceFormatter,
    },
    {
      key: 'workspaceOwner',
      name: 'Workspace Owner',
      minWidth: 160,
      formatter: WorkspaceOwnerFormatter,
    },
    {
      key: 'reportName',
      name: 'Report Name',
      minWidth: 130,
      formatter: ReportNameFormatter,
    }
  ];

  // Simplified column getters
  const getHealthyColumns = useCallback((): Array<Column<any>> => commonCapacityColumns, []);
  const getThrottledColumns = useCallback((): Array<Column<any>> => commonCapacityColumns, []);
  const getFailedRefreshColumns = useCallback((): Array<Column<any>> => failedRefreshColumn, []);
  const getRefreshesOver30MinColumns = useCallback((): Array<Column<any>> => commonDatasetColumns, []);
  const getXmlaReadsColumns = useCallback((): Array<Column<any>> => xmlaReadsColumns, []);

  const getColumns = useCallback((): Array<Column<any>> => {
    switch (activeKPI) {
      case 'Healthy':
        return getHealthyColumns();
      case 'Throttled':
        return getThrottledColumns();
      case 'Failed Refreshes':
        return getFailedRefreshColumns();
      case 'Refreshes > 30Mins':
        return getRefreshesOver30MinColumns();
      case 'XMLA Reads':
      case 'Export Operations':
        return getXmlaReadsColumns();
      default:
        return defaultColumns;
    }
  }, [activeKPI, getHealthyColumns, getThrottledColumns, getFailedRefreshColumns, getRefreshesOver30MinColumns, getXmlaReadsColumns]);

  // Get default column names for export
  const getDefaultColumnNames = useCallback((activeKPI: string | null): string[] => {
    switch (activeKPI) {
      case 'Healthy':
        return ['Capacity Id', 'Capacity Name', 'Status', 'SKU', 'Sector'];
      case 'Throttled':
        return ['Capacity Id', 'Capacity Name', 'Status', 'SKU', 'Sector'];
      case 'Failed Refreshes':
        return ['Dataset Name', 'Refresh Schedule', 'Last Refresh Duration', 'Capacity Name', 'Workspace Name', 'Refresh Status', 'Last Refresh Start (UTC)', 'Failed Reason' ];
      case 'Refreshes > 30Mins':
        return ['Dataset Name', 'Refresh Schedule', 'Last Refresh Duration', 'Capacity Name', 'Workspace Name', 'Refresh Status', 'Last Refresh Start (UTC)'];
      case 'XMLA Reads':
      case 'Export Operations':
        return ['Operation', 'User Email', 'Operation Time', 'Report Name', 'Workspace', 'Capacity Name', 'Dataset Name'];
      default:
        return ['Sector', 'Capacity', 'Capacity Owner', 'Capacity SKU', 'Workspace', 'Workspace Owner', 'Report Name'];
    }
  }, []);

  // Handle Capacity Selection from Autocomplete
  const handleCapacitySelectionChange = useCallback((event: any, newValue: CapacityOption | null) => {
    setSelectedCapacity(newValue);
    setPage(1);

    if (newValue) {
      console.log('Selected Capacity:', newValue);
    } else {
      console.log('Capacity selection cleared');
    }
  }, []);

  const handleCapacitySearchOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setCapacitySearchAnchorEl(event.currentTarget);
  }, []);

  const handleCapacitySearchClose = useCallback(() => {
    setCapacitySearchAnchorEl(null);
  }, []);

  const handleCapacityAutocompleteChange = useCallback((event: any, newValue: CapacityOption | null) => {
    handleCapacitySelectionChange(event, newValue);
    handleCapacitySearchClose();
  }, [handleCapacitySelectionChange, handleCapacitySearchClose]);

  const handleSorting = (sorting: any) => {
    console.log("sorting details", sorting);

    const fieldMap: Record<string, string> = {
      sector: "sector",
      capacity: "capacity",
      capacityOwner: "capacityOwner",
      workspaceOwner: "workspaceOwner",
      workspace: "workspace",
      capacitySKU: "capacitySKU",
      reportName: "reportName",
      timestamp: "timestamp",
    };

    const backendField = fieldMap[sorting.sortBy] || sorting.sortBy;

    setSortingInfo({ sortBy: backendField, sortDir: sorting.sortDir });
    setPage(1);
  };

  const handleFilter = (filter: { [key: string]: string[] | string | any[] }) => {
    const convertToUtcRange = (startMoment: any, endMoment: any) => {
      const utcStart = moment.utc([
        startMoment.year(), startMoment.month(), startMoment.date(),
        startMoment.hours(), startMoment.minutes(), startMoment.seconds()
      ]).toISOString();
      const utcEnd = moment.utc([
        endMoment.year(), endMoment.month(), endMoment.date(),
        endMoment.hours(), endMoment.minutes(), endMoment.seconds()
      ]).toISOString();
      return [utcStart, utcEnd];
    };

    const getFilterStatus = (columnKey: string) => {
      const inactiveColumns = ['capacityName', 'capacityId', 'sku'];
      return inactiveColumns.includes(columnKey) ? 'inactive' : 'active';
    };

    const filterListMap: { [key: string]: string | string[] } = {};
    const clearedColumns: string[] = [];

    Object.keys(filter).forEach((key) => {
      const value = filter[key];
  
      // Check if this filter is being cleared (empty array or null)
      if (value == null || (Array.isArray(value) && value.length === 0)) {
        clearedColumns.push(key);
        return;
      }
  
      // Special handling for operationTime date range
      if (key === 'operationTime' && Array.isArray(value) && value.length === 2) {
        filterListMap[key] = convertToUtcRange(value[0], value[1]);
      } else {
        // Normal filter
        filterListMap[key] = value;
      }
    });

    // Update the filterInfo state
    setFilterInfo(prev => {
      const newFilterInfo = { ...prev, ...filterListMap };

      // Remove cleared filters
      clearedColumns.forEach(col => {
        delete newFilterInfo[col];
      });
      
      return newFilterInfo;
    });

    setPage(1);

    // Refresh filter options for cleared columns
    if (clearedColumns.length > 0) {
      clearedColumns.forEach(async (columnKey) => {
        try {
          const status = getFilterStatus(columnKey);
          const response = await fetchUserActivityFilterOptions(columnKey, '', status);
          const options = response?.data || [];
  
          setFilterDataOptions((prev: any) => ({
            ...prev,
            [columnKey]: options
          }));
  
        } catch (error) {
          console.error(`Error refreshing filter options for ${columnKey}:`, error);
        }
      });
    }
  };
  

  const handleSearch = async (searchDetails: any, columnKey: any) => {
    if (searchDetails?.length >= 3) {
      try {
        let response;

        switch (columnKey) {
          case 'capacityName':
          case 'capacityId':
          case 'sku':
            response = await fetchUserActivityFilterOptions(columnKey, searchDetails, 'inactive');
            break;
          default:
            return;
        }

        const options = response?.data || [];

        setFilterDataOptions((prev: any) => ({
          ...prev,
          [columnKey]: options
        }));

      } catch (error) {
        console.error('Error performing search:', error);
      }
    } else if (searchDetails?.length === 0) {
      if (['capacityName', 'capacityId', 'sku'].includes(columnKey)) {
        const response = await fetchUserActivityFilterOptions(columnKey, '', 'inactive');
        const initialOptions = response?.data || [];
        setFilterDataOptions((prev: any) => ({
          ...prev,
          [columnKey]: initialOptions
        }));
      }
    }
  };

  const handlepageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const clearOtherFilters = () => {
    setSelectedCard('');
    setSelectedChartSegment('');
   
  };



  const handleChartTimeFilterChange = useCallback((newTimePeriod: string) => {
    setChartTimePeriod(newTimePeriod);
    fetchGrowthData(newTimePeriod);
  }, [fetchGrowthData]);

  // Export function
  const exportGetCapacityActivityDetails = async () => {
    const kpiConfig: { [key: string]: { kpiType: string; defaultSortBy: string; defaultOrderBy: string } } = {
      'Healthy': { kpiType: 'healthyCapacities', defaultSortBy: 'capacityName', defaultOrderBy: 'asc' },
      'Throttled': { kpiType: 'throttledCapacities', defaultSortBy: 'capacityName', defaultOrderBy: 'asc' },
      'Failed Refreshes': { kpiType: 'failedRefreshes', defaultSortBy: 'datasetName', defaultOrderBy: 'desc' },
      'Refreshes > 30Mins': { kpiType: 'refreshesOver30Min', defaultSortBy: 'lastRefreshDuration', defaultOrderBy: 'desc' },
      'XMLA Reads': { kpiType: 'xmlaReads', defaultSortBy: 'creationTime', defaultOrderBy: 'desc' },
      'Export Operations': { kpiType: 'exportOperations', defaultSortBy: 'creationTime', defaultOrderBy: 'desc' }
    };

    const config = kpiConfig[activeKPI];
    const exportColumns = getDefaultColumnNames(activeKPI);

    const cleanFilters = Object.fromEntries(
      Object.entries(filterInfo).filter(([_, v]) => Array.isArray(v) ? v.length > 0 : v != null)
    );

    const selectedSectors = checkedListRef?.current?.length > 0 
      ? checkedListRef?.current?.join(',') 
      : allSectors.join(',');

    const requestBody = {
      sector: selectedSectors,
      filterOption: filters?.filterOption || timeRange,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      sortBy: sortingInfo.sortBy || config.defaultSortBy,
      orderBy: sortingInfo.sortDir || config.defaultOrderBy,
      filters: cleanFilters,
      exportColumns: exportColumns,
      formatType: "EXCEL",
      capacityName: selectedCapacity?.capacityName || null,
      tableName: "",
      kpiType: config.kpiType
    };

    await fetchPowerBIExportCapacityDetails(requestBody);
  };

  const handleChartSegmentClick = useCallback((sector: string, month: string) => {
    const segmentId = `${sector}-${month}`;
    if (selectedChartSegment === segmentId) {
      setSelectedChartSegment('');
    } else {
      setSelectedChartSegment(segmentId);
    }
    clearOtherFilters();
    setPage(1);
  }, [selectedChartSegment]);

  const fetchCurrentKPIData = useCallback(async (currentPage: number) => {
    if (activeKPI) {
      await handleKPICardClick(activeKPI, true, currentPage);
    }
  }, [selectedCapacity, activeKPI, handleKPICardClick]);

  // ─── useEffect: fetch sectors (runs once on mount) ────────────────────────
  useEffect(() => {
    const fetchAllSectors = async () => {
      try {
        const response = await getPepwiseSectorList();
        if (response?.success && response?.data?.sectors) {
          const sectorNames = response.data.sectors
            .map((sector: any) => sector.Sector_Name)
            .filter((name: string) => name !== 'Unknown');
          setAllSectors(sectorNames);
          setSectorsLoaded(true);

          setFilterDataOptions((prev: any) => ({
            ...prev,
            sector: sectorNames
          }));
        }
      } catch (error) {
        console.error('Error fetching sectors:', error);
        setSectorsLoaded(true);
      }
    };
  
    fetchAllSectors();
  }, []);

  // ─── useEffect: fetch capacity names (runs once on mount) ─────────────────
  useEffect(() => {
    fetchCapacityOptionsFromAPI();
  }, []);

  // Load default Healthy data when sectors are loaded
  useEffect(() => {
    if (!sectorsLoaded || activeKPI) return;

    const loadCriticalThenFilters = async () => {
      console.log('🚀 Firing critical APIs in parallel...');

      // Fire all 3 critical APIs concurrently
      await Promise.allSettled([
        handleKPICardClick('Healthy', true, 1),   // fetchPowerBICapacityTableData
        fetchKPIData(),                             // fetchPowerBICapacityKpi
        fetchGrowthData()                           // fetchPowerBICapacityGrowthData
      ]);

      console.log('✅ Critical APIs complete — now fetching filter options');
      // Filter options are deferred until after critical APIs resolve
      fetchFilterOptions();
    };

    loadCriticalThenFilters();
  }, [sectorsLoaded]); // Only [sectorsLoaded] — runs exactly once when sectors load

  // ─── useEffect: subsequent dependency changes (skip initial mount) ─────────
  useEffect(() => {
    if (!sectorsLoaded) return;

    const currentState: FetchState = {
      page,
      pageSize,
      sortingInfo: JSON.stringify(sortingInfo),
      timeRange: timeRange || undefined,
      filterInfo: JSON.stringify(filterInfo),
      sector: filters?.sector,
      activeKPI,
      sectorsChange
    };

    const lastState = lastFetchRef.current;

    // Check if anything actually changed
    const hasChanges = (Object.keys(currentState) as (keyof FetchState)[]).some((key) => 
      currentState[key] !== lastState[key]
    );

    if (!hasChanges) return;

    const handleStateChange = () => {
      // Handle sector changes
      if (sectorsChange && sectorsChange.startsWith('sectors-') && sectorsChange !== lastState.sectorsChange) {
        setPage(1);
        fetchCurrentKPIData(1);
        fetchKPIData();
        fetchGrowthData();
        lastFetchRef.current = currentState;
        return;
      }

      // Handle page reset conditions
      const resetConditions = [
        timeRange !== lastState.timeRange,
        JSON.stringify(sortingInfo) !== lastState.sortingInfo,
        JSON.stringify(filterInfo) !== lastState.filterInfo,
        filters?.sector !== lastState.sector,
      ];

      if (resetConditions.some(Boolean)) {
        setPage(1);
        fetchCurrentKPIData(1);

        // Only fetch KPI and charts when relevant filters change
        const shouldFetchKPIAndCharts =
          timeRange !== lastState.timeRange || filters?.sector !== lastState.sector;

        if (shouldFetchKPIAndCharts) {
          fetchKPIData();
          fetchGrowthData();
        }
        lastFetchRef.current = currentState;
        return;
      }

      // Handle regular pagination changes
      if (page !== lastState.page || pageSize !== lastState.pageSize) {
        fetchCurrentKPIData(page);
      }

      // Update ref with current state
      lastFetchRef.current = currentState;
    };

    const timeoutId = setTimeout(handleStateChange, 150);

    return () => clearTimeout(timeoutId);
  }, [
    page,
    pageSize,
    sortingInfo,
    timeRange,
    filterInfo,
    filters?.sector,
    sectorsChange,
    activeKPI,
    sectorsLoaded,
    fetchCurrentKPIData,
    fetchKPIData,
    fetchGrowthData
  ]);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return; // skip — initial load is handled by the orchestrator above
    }
    if (!sectorsLoaded) return;

    fetchKPIData();
    if (activeKPI) fetchCurrentKPIData(1);
  }, [selectedCapacity]);



  // Memoize chart data to prevent unnecessary re-renders
  const memoizedCapacityGrowthData = useMemo(() => capacityGrowthData, [capacityGrowthData]);

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {/* First two cards - Capacity Health Status */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            <NumberWidget
              isLoading={isKpiLoading}
              title="Capacity Healthy"
              titleKey='Healthy'
              tooltipLabel="Total number of capacities having last 24 hours status as Healthy within the selected date range "
              hasTooltip={true}
              number={kpiData.healthyCapacities.value.toString()}
              change={kpiData.healthyCapacities.change}
              changeType={kpiData.healthyCapacities.changeType}
              bgColor={theme.palette?.widget?.background?.card}
              color={theme.palette?.widget?.text?.main}
              valueFontWeight={300}
              valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
              sameDecimalSize={true}
              selectedTitle={selectedCard}
              showChangePercentage={true}
              containerProps={{
                minHeight: '140px',
                sx: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(45, 46, 46, 0.3)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                },
                onClick: () => handleKPICardClick('Healthy', true, 1)
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ position: 'relative' }}>
            <NumberWidget
              isLoading={isKpiLoading}
              title="Capacity Throttled"
              titleKey='Throttled'
              tooltipLabel="Total number of capacities having last 24 hours status as Throttling within the selected date range."
              hasTooltip={true}
              number={kpiData.throttledCapacities.value.toString()}
              change={kpiData.throttledCapacities.change}
              changeType={kpiData.throttledCapacities.changeType}
              bgColor={theme.palette?.widget?.background?.card}
              color={theme.palette?.widget?.text?.main}
              valueFontWeight={300}
              valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
              sameDecimalSize={true}
              selectedTitle={selectedCard}
              showChangePercentage={true}
              containerProps={{
                minHeight: '140px',
                sx: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(45, 46, 46, 0.3)',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                },
                onClick: () => handleKPICardClick('Throttled', true, 1)
              }}
            />
          </Box>
        </Grid>

        {/* Third card - Capacity Growth Chart */}
        <Grid item xs={12} md={6}>
          <CapacityGrowthChart
            data={memoizedCapacityGrowthData}
            onSegmentClick={handleChartSegmentClick}
            selectedSegment={selectedChartSegment}
            isLoading={isGrowthLoading}
            onTimeFilterChange={handleChartTimeFilterChange}
          />
        </Grid>

        {/* Fourth card - Fabric Capacity Metrics with NumberWidget cards */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              borderRadius: '4px',
              background: theme.palette?.kpiTile?.background?.hover,
              padding: '24px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Typography
                variant="subHeader"
                sx={{
                  color: theme.palette?.widget?.text?.main,
                  lineHeight: '24px',
                  fontFamily: 'Montserrat'
                }}
              >
                Fabric Capacity Metrics
              </Typography>

              {/* Autocomplete for capacity selection */}
              <Box>
                <Box
                  onClick={handleCapacitySearchOpen}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer'
                  }}
                >
                  <Typography
                    variant="bodyText"
                    sx={{
                      color: theme.palette?.DropdownV2?.dropdownIconDefault,
                      fontWeight: 500
                    }}
                  >
                    {selectedCapacity?.capacityName || 'Search'}
                  </Typography>
                  <KeyboardArrowDownIcon
                    sx={{
                      color: theme.palette?.DropdownV2?.dropdownIconDefault,
                      fontSize: '20px',
                      transform: capacitySearchAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </Box>
                <Popper
                  open={Boolean(capacitySearchAnchorEl)}
                  anchorEl={capacitySearchAnchorEl}
                  placement="bottom-end"
                  sx={{
                    zIndex: 1300,
                    width: '350px'
                  }}
                >
                  <ClickAwayListener onClickAway={handleCapacitySearchClose}>
                    <Box sx={{ width: '350px' }}>
                      <Autocomplete
                        open={true}
                        disablePortal
                        value={null}
                        onChange={handleCapacityAutocompleteChange}
                        options={capacityOptions}
                        getOptionLabel={(option) => option.capacityName}
                        loading={capacityOptionsLoading}
                        isOptionEqualToValue={(option, value) => option.capacityId === value.capacityId}
                        forcePopupIcon={false}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search Capacity"
                            variant="outlined"
                            autoFocus
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: theme.palette?.DropdownV2?.dropdownIconDefault }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <>
                                  {capacityOptionsLoading && (
                                    <CircularProgress size={16} thickness={5} sx={{ color: theme.palette?.DropdownV2?.dropdownIconDefault }} />
                                  )}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                            sx={{
                              width: '350px',
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '4px',
                                backgroundColor: theme.palette?.DropdownV2?.background,
                                color: theme.palette?.DropdownV2?.menuTextColor,
                                '& fieldset': {
                                  borderColor: theme.palette?.DropdownV2?.borderColor,
                                },
                                '&:hover fieldset': {
                                  borderColor: theme.palette?.DropdownV2?.borderColorHover,
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.palette?.DropdownV2?.borderColorActive,
                                },
                              },
                              '& .MuiInputBase-input': {
                                color: `${theme.palette?.DropdownV2?.menuTextColor} !important`,
                                '&::placeholder': {
                                  ...theme.typography.filterText,
                                  color: `${theme.palette?.DropdownV2?.mutedText} !important`,
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          const selected = selectedCapacity?.capacityId === option.capacityId;

                          return (
                            <Box
                              component="li"
                              {...props}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: selected
                                  ? `${theme.palette?.DropdownV2?.menuItemSelectedBg} !important`
                                  : `${theme.palette?.DropdownV2?.menuItemBackground} !important`,
                                color: selected
                                  ? `${theme.palette?.DropdownV2?.menuItemSelectedText} !important`
                                  : `${theme.palette?.DropdownV2?.menuItemText} !important`,
                                '&:hover': {
                                  backgroundColor: `${theme.palette?.DropdownV2?.menuItemHover} !important`,
                                  color: `${theme.palette?.DropdownV2?.menuItemHoverText} !important`
                                }
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="bodyText"
                                  sx={{
                                    display: 'block',
                                    color: selected
                                      ? `${theme.palette?.DropdownV2?.menuItemSelectedText} !important`
                                      : `${theme.palette?.DropdownV2?.menuItemText} !important`
                                  }}
                                >
                                  {option.capacityName}
                                </Typography>
                                <Typography
                                  variant="bodyText"
                                  sx={{
                                    display: 'block',
                                    color: `${theme.palette?.DropdownV2?.mutedText} !important`
                                  }}
                                >
                                  {option.capacityId}
                                </Typography>
                              </Box>
                              {selected && (
                                <CheckIcon
                                  sx={{
                                    color: `${theme.palette?.DropdownV2?.tickColor} !important`,
                                    fontSize: '18px',
                                    flexShrink: 0,
                                    ml: 1
                                  }}
                                />
                              )}
                            </Box>
                          );
                        }}
                        ListboxProps={{
                          style: {
                            backgroundColor: theme.palette?.DropdownV2?.menuItemBackground,
                            maxHeight: '300px',
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        }}
                        loadingText={
                          <Typography sx={{ color: theme.palette?.DropdownV2?.menuItemText }}>
                            Loading Capacity...
                          </Typography>
                        }
                        noOptionsText={
                          <Typography sx={{ color: theme.palette?.DropdownV2?.menuItemText }}>
                            No capacities found
                          </Typography>
                        }
                        componentsProps={{
                          popper: {
                            sx: {
                              '& .MuiAutocomplete-paper *': {
                                color: `${theme.palette?.DropdownV2?.menuItemText} !important`,
                              },
                              '& .capacity-option-id': {
                                color: `${theme.palette?.DropdownV2?.mutedText} !important`,
                              },
                              '& .capacity-selected-tick': {
                                color: `${theme.palette?.DropdownV2?.tickColor} !important`,
                                fill: `${theme.palette?.DropdownV2?.tickColor} !important`,
                              },
                            },
                          },
                          paper: {
                            sx: {
                              backgroundColor: `${theme.palette?.DropdownV2?.menuItemBackground} !important`,
                            },
                          },
                        }}
                      />
                    </Box>
                  </ClickAwayListener>
                </Popper>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {subCardsData && subCardsData.length > 0 ? subCardsData.map((card, index) => {
                const clickableTitles = ['XMLA Reads', 'Refreshes > 30Mins', 'Failed Refreshes', 'Export Operations'];
                const isClickable = clickableTitles.includes(card.title);

                return (
                  <Grid item xs={12} sm={6} md={4} key={card.title} sx={{ display: 'flex' }}>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <NumberWidget
                        isLoading={isKpiLoading}
                        title={card.title}
                        titleKey={card.title}
                        tooltipLabel={card.toolTipText}
                        hasTooltip={true}
                        number={card.value}
                        units={card.unit || undefined}
                        changeType={card.changeType}
                        bgColor={theme.palette?.widget?.background?.card}
                        color={theme.palette?.widget?.text?.main}
                        valueFontWeight={300}
                        valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
                        sameDecimalSize={true}
                        showChangePercentage={false}
                        selectedTitle={selectedCard}
                        containerProps={{
                          sx: {
                            borderRadius: '4px',
                            minWidth: 'fit-content',
                            width: '100%',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? {
                              boxShadow: '0 4px 12px rgba(45, 46, 46, 0.3)',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s ease-in-out'
                            } : {}
                          },
                          onClick: isClickable ? () => handleKPICardClick(card.title, true, 1) : undefined
                        }}
                      />
                    </Box>
                  </Grid>
                );
              }) : (
                (() => {
                  const loadingItems = [];
                  const loadingTitles = [
                    'Scheduled Refreshes', 'On-Demand Refreshes', 'Failed Refreshes',
                    'Refreshes > 30Mins', 'Memory Config', 'Max Dataset Size',
                    'Concurrent Users', 'XMLA Reads', 'Export Operations'
                  ];

                  for (let i = 0; i < loadingTitles.length; i++) {
                    const title = loadingTitles[i] || `Metric ${i + 1}`;
                    loadingItems.push(
                    <Grid item xs={12} sm={6} md={4} key={`loading-${title}`} sx={{ display: 'flex' }}>
                      <NumberWidget
                        isLoading={isKpiLoading}
                        title="Loading..."
                        tooltipLabel=""
                        hasTooltip={true}
                        number="0"
                        bgColor={theme.palette?.widget?.background?.card}
                        color={theme.palette?.widget?.text?.main}
                        valueFontWeight={300}
                        valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
                        sameDecimalSize={true}
                        showChangePercentage={false}
                        containerProps={{
                          sx: {
                            borderRadius: '4px',
                            minWidth: 'fit-content',
                            width: '100%',
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                          }
                        }}
                      />
                    </Grid>
                    );
                  }
                  return loadingItems;
                })()
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Table Section */}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={12}>
            <Box sx={{ width: '100%' }}>
              {kpiTableLoading ? (
                <Stack justifyContent="center" alignItems="center" minHeight="100px">
                  <LoaderComponent />
                </Stack>
                ) : (
                <CustomizedDataGrid
                  title={kpiTableTitle}
                  tableColumns={getColumns()}
                  tableData={activeKPI ? kpiTableData : []}
                  tableType={`capacity-${activeKPI ? activeKPI.toLowerCase().replace(/\s+/g, '-') : 'monitoring'}`}
                  defaultColumnNames={getDefaultColumnNames(activeKPI)}
                  onSorting={handleSorting}
                  currSorting={sortingInfo}
                  filterDataOptions={filterDataOptions}
                  onFilterCheck={handleFilter}
                  currFilter={filterInfo}
                  onFilterSearch={handleSearch}
                  page={page}
                  pageSize={pageSize}
                  paginationData={paginationData}
                  listLength={activeKPI ? kpiTableData.length : 0}
                  handlepageSizeChange={handlepageSizeChange}
                  handlePageChange={handlePageChange}
                  isDateRangeFilter={true}
                  isDisabled={false}
                  isFetching={kpiTableLoading}
                  showExport={true}
                  sectorsList={allSectors}
                  fileName={`capacity-${activeKPI ? activeKPI.toLowerCase().replace(/\s+/g, '-') : 'activity'}-report`}
                  exportTableData={() => exportGetCapacityActivityDetails()}
                  
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};


export default CapacityTab;